/**
 * ArchiveConfirmSheet — BottomSheet confirm for archive / restore actions
 * (Block 6.F.5 §5.2).
 *
 * Third consumer of the BottomSheet primitive (after FilterBar in 6.F.2 +
 * the suggest-edit picker in 6.F.4). Headline is question form ("Archive
 * Bayshore?"); body reassures non-permanence; action button is verb-led
 * ("Archive" / "Restore").
 *
 * Color discipline per peace-hub aesthetic + 6.F.4 §3.7:
 *   - Archive action button: bg-warm-500 hover:bg-warm-600 (warm-orange
 *     for caution; red ONLY for true error states).
 *   - Restore action button: bg-sage-600 hover:bg-sage-700 (sage-primary,
 *     encouraging — restoration is a positive action).
 *
 * F1 (no separate modal layer) per ADR-017: BottomSheet IS the modal
 * pattern; backdrop click + ESC close.
 */

import { useState, type ReactNode } from 'react';
import { BottomSheet } from './primitives/BottomSheet';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface ArchiveConfirmSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** What we're acting on. */
  targetName: string;
  /** Whether this is an archive (default) or restore action. */
  mode: 'archive' | 'restore';
  /**
   * Fire when the user confirms. Should resolve on success / reject on
   * failure; the sheet shows a "saving" state during the promise.
   */
  onConfirm: () => Promise<void>;
}

export function ArchiveConfirmSheet({
  isOpen,
  onClose,
  targetName,
  mode,
  onConfirm,
}: ArchiveConfirmSheetProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  const isArchive = mode === 'archive';
  const headline = isArchive ? `Archive ${targetName}?` : `Restore ${targetName}?`;
  const body = isArchive
    ? `This hides ${targetName} from search and filters. You can restore them later from this same screen.`
    : `This brings ${targetName} back to search and filters.`;
  const actionLabel = isArchive ? 'Archive' : 'Restore';
  const actionPending = isArchive ? 'Archiving…' : 'Restoring…';
  const actionClasses = isArchive
    ? 'bg-warm-500 hover:bg-warm-600 text-cream'
    : 'bg-sage-600 hover:bg-sage-700 text-cream';

  async function handleConfirm(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(pageWriteText(e, contact));
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={busy ? () => undefined : onClose} title={headline}>
      <div className="px-5 py-4">
        <p className="text-sm text-ink mb-4">{body}</p>

        {error && (
          <div role="alert" className="text-sm text-red-700 mb-3" data-testid="archive-confirm-error">
            ⚠ {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-11 px-4 py-2 text-sm text-ink hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={
              'min-h-11 px-4 py-2 text-sm font-semibold rounded-md disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
              actionClasses
            }
          >
            {busy ? actionPending : actionLabel}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
